import React, { useEffect, useRef } from 'react';
import { Alert } from '@mui/lab';

/**
 * FloatingMessage - Standardized top-right floating notification for the whole app.
 *
 * Renders a fixed-position, filled MUI Alert that floats over the content (never pushes
 * layout) and auto-dismisses after `autoHideDuration` ms. Uses a ref for the close
 * callback so parent re-renders never reset the auto-dismiss timer.
 *
 * Props:
 *   open               - Boolean controlling visibility
 *   message            - Text to display
 *   severity           - 'success' | 'error' | 'warning' | 'info' (default 'error')
 *   onClose            - Callback invoked when the message dismisses
 *   autoHideDuration   - Milliseconds before auto-dismiss (default 4000)
 */
function FloatingMessage({ open, message, severity = 'error', onClose, autoHideDuration = 4000 }) {
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    useEffect(() => {
        if (!open || !message) return undefined;
        const timer = setTimeout(() => {
            if (onCloseRef.current) onCloseRef.current();
        }, autoHideDuration);
        return () => clearTimeout(timer);
    }, [open, message, severity, autoHideDuration]);

    if (!open || !message) return null;

    return (
        <Alert
            elevation={6}
            variant="filled"
            severity={severity}
            onClose={() => onCloseRef.current && onCloseRef.current()}
            sx={{
                position: 'fixed',
                top: 96,
                right: 20,
                zIndex: 1400,
                minWidth: 300,
                maxWidth: 480,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                '& .MuiAlert-action .MuiIconButton-root': {
                    color: '#fff',
                    backgroundColor: 'rgba(255,255,255,0.25)',
                    borderRadius: '50%',
                    padding: '2px',
                    '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.45)',
                    },
                },
                '& .MuiAlert-action .MuiSvgIcon-root': {
                    fontSize: '1.1rem',
                },
            }}
        >
            {message}
        </Alert>
    );
}

export default FloatingMessage;
